import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./ProductScreenMobile.css";

import PurchaseBox from "./PurchaseBox";
import Meta from "../../components/Meta";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import ImageZoom from "../../components/images/ImageZoom";

import {
  getOptionVisualSwatch,
  getProductVisualSwatch,
  getVariantVisualSwatch,
  safeText,
  titleCase,
} from "../../components/swatches/productSwatches";

import {
  getProductByIdentifier,
  resetProductBySlug,
  resetProductDetails,
  resetProductResolve,
} from "../../actions/productActions";

import { addToCart, resetCartError } from "../../actions/cartActions";
import { selectMadeToMeasureDraftProduct } from "../../actions/madeToMeasureActions";
import ProductRecommendations from "./ProductRecommendations";
import { getSafeUserMessage } from "../../utils/errorMessages";
import { SITE_NAME } from "../../constants/brand";
import {
  buildProductSeo,
  buildProductSeoDescription,
  buildProductSeoKeywords,
  buildProductSeoTitle,
} from "../../utils/productSeo";
import {
  getFirstPositivePrice,
  getProductDisplayPriceValue,
} from "../../utils/productPricing";
import {
  MTM_STEPS,
  getMtmEligibilityForStep,
  getMtmReturnStepAfterSelection,
  getMtmSelectionActionLabel,
  getMtmShopForStep,
  normalizeMtmStep,
} from "../../utils/madeToMeasureJourney";
import { TEXTILE_SHOP_KEY } from "../../constants/shopMeta";

const MAX_QTY_CAP = 20;
const FALLBACK_BRAND = SITE_NAME;
const FALLBACK_IMAGE = "https://placehold.co/900x1200?text=Academy+Costumes+London";

const SHOP_LABELS = Object.freeze({
  "ready-made": "Ready-Made",
  readyMade: "Ready-Made",
  standard: "Ready-Made",
  textile: "Textile",
  textiles: "Textile",
  haberdashery: "Haberdashery",
});

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function getErrorText(error) {
  if (!error) return "";
  if (typeof error === "string") {
    return getSafeUserMessage(
      error,
      "We could not load this product right now. Please try again later.",
    );
  }

  return getSafeUserMessage(
    error.customerMessage || error.message || error.error,
    "We could not load this product right now. Please try again later.",
  );
}

function toPositiveInt(value, fallback = 1) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function normalizeChoiceKey(value = "") {
  return safeText(value).toLowerCase().replace(/\s+/g, " ").trim();
}

function uniqueTextValues(values = []) {
  const seen = new Set();
  const output = [];

  values.forEach((value) => {
    const text = safeText(value);
    const key = normalizeChoiceKey(text);

    if (!text || !key || seen.has(key)) return;

    seen.add(key);
    output.push(text);
  });

  return output;
}

function getImageUrl(image) {
  if (typeof image === "string") return safeText(image);
  if (!image || typeof image !== "object") return "";

  return safeText(
    image.url ||
      image.src ||
      image.image ||
      image.imageUrl ||
      image.defaultImage ||
      image.defaultImageUrl ||
      image.secure_url ||
      image.secureUrl ||
      image.thumbnail ||
      image.thumbnailUrl ||
      "",
  );
}

function uniqueImages(items = [], productName = "") {
  const seen = new Set();
  const output = [];

  items.forEach((item) => {
    const url = getImageUrl(item);
    if (!url || seen.has(url)) return;

    seen.add(url);

    output.push({
      ...(typeof item === "object" && item ? item : {}),
      url,
      src: url,
      image: url,
      imageUrl: url,
      alt:
        typeof item === "object" && item?.alt
          ? safeText(item.alt)
          : productName || "Product image",
    });
  });

  return output;
}

function buildProductGallery(product = {}) {
  return uniqueImages(
    [
      ...(Array.isArray(product.images) ? product.images : []),
      product.defaultImage,
    ],
    product.name,
  );
}

function buildSelectionGallery(
  selection = null,
  productGallery = [],
  productName = "",
) {
  const selectionName =
    safeText(selection?.name) || safeText(selection?.label) || productName;
  const selectionGallery = uniqueImages(
    Array.isArray(selection?.images) ? selection.images : [],
    selectionName,
  );

  if (selectionGallery.length) return selectionGallery;

  const productImageUrls = new Set(productGallery.map((image) => image.url));
  return uniqueImages(
    [
      selection?.image,
      selection?.imageUrl,
      selection?.defaultImage,
      selection?.defaultImageUrl,
    ],
    selectionName,
  ).filter((image) => !productImageUrls.has(image.url));
}

function buildGallery(product = {}, selectedSelection = null) {
  const productGallery = buildProductGallery(product);
  const selectionGallery = buildSelectionGallery(
    selectedSelection,
    productGallery,
    product.name,
  );

  if (selectionGallery.length) return selectionGallery;

  if (productGallery.length) return productGallery;

  return [
    {
      url: FALLBACK_IMAGE,
      src: FALLBACK_IMAGE,
      alt: product.name || "Product image",
    },
  ];
}

function getOptionParams(option) {
  const safeOption = asObject(option);
  const value = asObject(safeOption.value);

  return {
    variantSku: safeText(
      safeOption.variantSku || value.variantSku,
    ).toUpperCase(),

    variantType: safeText(
      safeOption.variantType || value.variantType,
    ).toLowerCase(),
  };
}

function getOptionKey(option) {
  const safeOption = asObject(option);
  const params = getOptionParams(safeOption);

  return [
    params.variantSku || "no-sku",
    params.variantType || "standard",
    safeText(safeOption.label) || "option",
  ].join("::");
}

function isOptionDisplayable(option = {}) {
  return option.isDisplayable !== false && option.displayInOptions !== false;
}

function isUnavailablePurchaseState(option = {}) {
  const state = safeText(
    option.purchaseState ||
      option.unavailableState ||
      option.reason ||
      option.status ||
      option.stockStatus,
  ).toUpperCase();

  const status = safeText(option.status || option.stockStatus).toLowerCase();

  return (
    [
      "RESERVED",
      "SOLD_OUT",
      "UNAVAILABLE",
      "MAX_IN_CART",
      "COMING_SOON",
      "INSUFFICIENT_STOCK",
      "REQUESTED_QTY_EXCEEDS_AVAILABLE",
      "RESERVED_BY_ANOTHER_CUSTOMER",
      "SHARED_STOCK_BLOCKED_BY_CART",
    ].includes(state) ||
    [
      "reserved",
      "sold_out",
      "out_of_stock",
      "unavailable",
      "max_in_cart",
      "coming_soon",
      "insufficient_stock",
      "requested_qty_exceeds_available",
      "shared_stock_blocked_by_cart",
      "shared_stock_exhausted_in_cart",
    ].includes(status)
  );
}

function getOptionPurchaseState(option = {}) {
  return safeText(
    option.purchaseState ||
      option.unavailableState ||
      option.reason ||
      option.status ||
      option.stockStatus,
  ).toUpperCase();
}

function getOptionPurchaseStatus(option = {}) {
  return safeText(option.status || option.stockStatus).toLowerCase();
}

function isOptionSoldOut(option = {}) {
  const state = getOptionPurchaseState(option);
  const status = getOptionPurchaseStatus(option);

  return (
    state === "SOLD_OUT" ||
    status === "out_of_stock" ||
    status === "sold_out" ||
    option.soldOut === true
  );
}

function isOptionHardUnavailable(option = {}) {
  const state = getOptionPurchaseState(option);
  const status = getOptionPurchaseStatus(option);

  return (
    state === "UNAVAILABLE" ||
    status === "unavailable" ||
    state === "COMING_SOON" ||
    status === "coming_soon"
  );
}

function isOptionReserved(option = {}) {
  const state = getOptionPurchaseState(option);
  const status = getOptionPurchaseStatus(option);

  return (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    status === "reserved"
  );
}

function isOptionSelectable(option) {
  if (!option) return false;
  if (!isOptionDisplayable(option)) return false;

  if (option.canSelect === true || option.isSelectable === true) return true;
  if (option.canSelect === false || option.isSelectable === false) return false;
  if (isOptionHardUnavailable(option)) return false;
  if (isOptionReserved(option)) return false;
  if (isOptionSoldOut(option)) return false;
  if (option.disabled === true) return false;

  const state = getOptionPurchaseState(option);
  const status = getOptionPurchaseStatus(option);

  if (
    state === "INSUFFICIENT_STOCK" ||
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock"
  ) {
    return true;
  }

  if (
    option.isAddable === true ||
    option.canAddToCart === true ||
    option.isAvailable === true
  ) {
    return true;
  }

  const maxAddableQty = Number(option.maxAddableQty);
  if (Number.isFinite(maxAddableQty) && maxAddableQty > 0) return true;

  const available = Number(option.available);
  if (Number.isFinite(available) && available > 0) return true;

  return true;
}

function isOptionAddable(option) {
  if (!option) return false;
  if (!isOptionDisplayable(option)) return false;
  if (!isOptionSelectable(option)) return false;
  if (isUnavailablePurchaseState(option)) return false;

  if (option.disabled === true) return false;
  if (option.isAddable === false) return false;
  if (option.canAddToCart === false) return false;
  if (option.isAvailable === false) return false;

  if (
    option.isAddable === true ||
    option.canAddToCart === true ||
    option.isAvailable === true
  ) {
    return true;
  }

  const maxAddableQty = Number(option.maxAddableQty);
  if (Number.isFinite(maxAddableQty) && maxAddableQty <= 0) return false;

  const available = Number(option.available);
  if (Number.isFinite(available) && available <= 0) return false;

  return true;
}

function hasExplicitAddableFlag(option) {
  return (
    option?.isAddable === true ||
    option?.canAddToCart === true ||
    option?.isAvailable === true
  );
}

function optionMatchesQuery(option = {}, searchParams) {
  const params = getOptionParams(option);
  const querySku = safeText(searchParams.get("variantSku")).toUpperCase();
  const queryType = safeText(searchParams.get("variantType")).toLowerCase();

  if (!querySku && !queryType) return false;
  if (querySku && params.variantSku !== querySku) return false;
  if (queryType && params.variantType !== queryType) return false;

  return true;
}

function optionMatchesVariant(option = {}, variant = {}) {
  const params = getOptionParams(option);
  const variantSku = safeText(variant.variantSku || variant.sku).toUpperCase();
  const variantType = safeText(variant.variantType).toLowerCase();

  if (variantSku || params.variantSku) {
    return Boolean(
      variantSku && params.variantSku && variantSku === params.variantSku,
    );
  }

  return Boolean(
    variantType && params.variantType && variantType === params.variantType,
  );
}

function getVariantForOption(product = {}, option = null) {
  if (!option) return null;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  return (
    variants.find((variant) => optionMatchesVariant(option, variant)) || null
  );
}

function hasColourData(item = {}) {
  const singleValues = [
    item.color,
    item.colour,
    item.colorName,
    item.colourName,
    item.colorwayLabel,
    item.colourwayLabel,
  ];

  const arrayValues = [
    item.colors,
    item.colours,
    item.colorway,
    item.colourway,
    item.palette,
  ];

  return (
    singleValues.some((value) => safeText(value)) ||
    arrayValues.some((value) => Array.isArray(value) && value.length > 0)
  );
}

function getOptionColourLabel(option) {
  const safeOption = asObject(option);

  return safeText(
    safeOption.color ||
      safeOption.colour ||
      safeOption.colorName ||
      safeOption.colourName ||
      safeOption.colorwayLabel ||
      safeOption.colourwayLabel,
  );
}

function getOptionSizeLabel(option) {
  const safeOption = asObject(option);
  return safeText(safeOption.size);
}

function getOptionColourKey(option) {
  return normalizeChoiceKey(getOptionColourLabel(option));
}

function getOptionSizeKey(option) {
  return normalizeChoiceKey(getOptionSizeLabel(option));
}

function getSwatchSignature(swatch = {}) {
  if (swatch.kind === "image") return `image:${swatch.value}`;

  return `${swatch.kind}:${(swatch.rawValues || swatch.values || [])
    .map((value) => safeText(value).toLowerCase())
    .join("|")}`;
}

function addChoiceItem(map, { key, label, swatch, option, image }) {
  const safeKey = normalizeChoiceKey(key || label);
  if (!safeKey) return;

  const optionKey = option ? getOptionKey(option) : "";
  const existing = map.get(safeKey);

  if (existing) {
    if (optionKey && !existing.optionKeys.includes(optionKey)) {
      existing.optionKeys.push(optionKey);
    }

    if (isOptionSelectable(option)) {
      existing.isSelectable = true;
    }

    if (isOptionAddable(option)) {
      existing.isAddable = true;
    }

    return;
  }

  map.set(safeKey, {
    key: safeKey,
    label: titleCase(label || key),
    ariaLabel: `Select ${titleCase(label || key)}`,
    swatch,
    optionKeys: optionKey ? [optionKey] : [],
    image,
    isSelectable: option ? isOptionSelectable(option) : true,
    isAddable: option ? isOptionAddable(option) : true,
  });
}

function buildColourItems(product = {}, options = []) {
  const optionMap = new Map();
  const useVariantSwatches = Boolean(
    product?.isVariant &&
      options.some(
        (option) =>
          getOptionColourLabel(option) || safeText(option?.swatchImage),
      ),
  );

  options.filter(isOptionDisplayable).forEach((option) => {
    const colourLabel = getOptionColourLabel(option);
    const label =
      colourLabel ||
      (useVariantSwatches ? safeText(option.label) : "");
    if (!label) return;

    addChoiceItem(optionMap, {
      key:
        safeText(option.swatchImage) ||
        safeText(option.variantSku) ||
        colourLabel ||
        label,
      label: safeText(option.label) || label,
      swatch: getOptionVisualSwatch(option),
      option,
      image: option.image,
    });
  });

  const optionItems = Array.from(optionMap.values());

  if (optionItems.length) return optionItems;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variantMap = new Map();

  variants.forEach((variant, index) => {
    if (!hasColourData(variant)) return;

    const swatch = getVariantVisualSwatch(variant);
    if (!swatch?.colourCount && swatch?.kind !== "image") return;

    const matchingOption = options.find((option) =>
      optionMatchesVariant(option, variant),
    );

    const label =
      safeText(variant.color) ||
      safeText(variant.colour) ||
      safeText(variant.name) ||
      swatch.label ||
      `Colour ${index + 1}`;

    addChoiceItem(variantMap, {
      key: getSwatchSignature(swatch) || label,
      label,
      swatch,
      option: matchingOption,
      image:
        variant.defaultImage ||
        getImageUrl(Array.isArray(variant.images) ? variant.images[0] : ""),
    });
  });

  const variantItems = Array.from(variantMap.values());

  if (variantItems.length) return variantItems;

  const productSwatch = getProductVisualSwatch(product);

  if (productSwatch?.colourCount || productSwatch?.kind === "image") {
    return [
      {
        key: "product-colourway",
        label: productSwatch.label || "Colourway",
        ariaLabel: productSwatch.label || "Product colourway",
        swatch: productSwatch,
        optionKeys: [],
        image: product.defaultImage,
        isAddable: true,
      },
    ];
  }

  return [];
}

function buildSizeItems(options = []) {
  const map = new Map();

  options.filter(isOptionDisplayable).forEach((option) => {
    const label =
      getOptionSizeLabel(option) || option.label || option.unitLabel;
    if (!label) return;

    addChoiceItem(map, {
      key: label,
      label,
      option,
      image: option.image,
    });
  });

  return Array.from(map.values());
}

function getOptionsForChoice(item = {}, options = []) {
  const keys = Array.isArray(item.optionKeys) ? item.optionKeys : [];

  return keys
    .map((key) => options.find((option) => getOptionKey(option) === key))
    .filter(Boolean);
}

function pickBestOption(candidates = [], selectedOption = null) {
  if (!candidates.length) return null;

  const displayableCandidates = candidates.filter(isOptionDisplayable);
  const selectableCandidates = displayableCandidates.filter(isOptionSelectable);
  const addableCandidates = selectableCandidates.filter(isOptionAddable);

  const pool =
    addableCandidates.length ||
    selectableCandidates.length ||
    displayableCandidates.length
      ? addableCandidates.length
        ? addableCandidates
        : selectableCandidates.length
          ? selectableCandidates
          : displayableCandidates
      : candidates;

  const selectedColourKey = getOptionColourKey(selectedOption);
  const selectedSizeKey = getOptionSizeKey(selectedOption);
  const selectedVariantType = getOptionParams(selectedOption).variantType;

  return (
    pool.find(
      (option) =>
        selectedColourKey &&
        selectedSizeKey &&
        getOptionColourKey(option) === selectedColourKey &&
        getOptionSizeKey(option) === selectedSizeKey,
    ) ||
    pool.find(
      (option) =>
        selectedSizeKey && getOptionSizeKey(option) === selectedSizeKey,
    ) ||
    pool.find(
      (option) =>
        selectedColourKey && getOptionColourKey(option) === selectedColourKey,
    ) ||
    pool.find(
      (option) =>
        selectedVariantType &&
        getOptionParams(option).variantType === selectedVariantType,
    ) ||
    pool[0] ||
    null
  );
}

function getProductFromState(productState = {}) {
  return (
    productState.product ||
    productState.item ||
    productState.data ||
    productState.productDetails ||
    null
  );
}

function getDescriptionParagraphs(description = "") {
  return safeText(description)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatProductType(value = "") {
  const cleanValue = safeText(value);
  const key = normalizeChoiceKey(cleanValue);

  if (!key) return "";
  if (SHOP_LABELS[cleanValue]) return SHOP_LABELS[cleanValue];
  if (SHOP_LABELS[key]) return SHOP_LABELS[key];

  return titleCase(cleanValue);
}

function getPurchaseOptionLabel(product = {}) {
  const type = safeText(product.productType).toLowerCase();
  const shopKey = safeText(product.shopKey).toLowerCase();

  if (type === "textile" || shopKey === TEXTILE_SHOP_KEY) {
    return "Purchase option";
  }
  if (type === "haberdashery" || shopKey === "haberdashery") return "Selection";

  return "Size";
}

function buildSpecificationRows(product = {}, selectedOption = null) {
  const readyMade = product.readyMade || {};
  const textile = product.textile || {};
  const haberdashery = product.haberdashery || {};

  const selectedColours = uniqueTextValues([
    ...(Array.isArray(selectedOption?.colors) ? selectedOption.colors : []),
    selectedOption?.color || selectedOption?.colour,
  ]);
  const colours = selectedColours.length
    ? selectedColours
    : uniqueTextValues([
        ...(Array.isArray(product.colors) ? product.colors : []),
        product.color,
      ]);

  const selectedMaterials = uniqueTextValues([
    ...(Array.isArray(selectedOption?.materials)
      ? selectedOption.materials
      : []),
    selectedOption?.material,
  ]);
  const materials = selectedMaterials.length
    ? selectedMaterials
    : uniqueTextValues([
        ...(Array.isArray(product.materials) ? product.materials : []),
        product.material,
      ]);

  const selectedSizes = uniqueTextValues([
    ...(Array.isArray(selectedOption?.sizes) ? selectedOption.sizes : []),
    selectedOption?.size,
  ]);
  const sizes = selectedSizes.length
    ? selectedSizes
    : uniqueTextValues([
        ...(Array.isArray(product.sizes) ? product.sizes : []),
        product.size,
      ]);

  const rows = [
    ["Product type", formatProductType(product.productType || product.shopKey)],
    ["Colour", colours.join(", ")],
    ["Material", materials.join(", ")],
    [
      "Pattern",
      Array.isArray(product.patterns)
        ? uniqueTextValues(product.patterns).join(", ")
        : "",
    ],
    ["Size", sizes.join(", ")],
    ["Origin", product.origin],
    ["Best For", product.usage],
    ["Care", product.careInstructions],
    ["Finish", haberdashery.finish],
    [
      "Width",
      haberdashery.widthMm !== null && haberdashery.widthMm !== undefined
        ? `${haberdashery.widthMm} mm`
        : "",
    ],
    [
      "Diameter",
      haberdashery.diameterMm !== null && haberdashery.diameterMm !== undefined
        ? `${haberdashery.diameterMm} mm`
        : "",
    ],
    ["Compatibility", haberdashery.compatibility],
    ["Era", readyMade.era],
    ["Fit", readyMade.fit],
    ["Silhouette", readyMade.silhouette],
    ["Closure", readyMade.closure],
    [
      "Unit",
      selectedOption?.unitLabel || textile.unitType || haberdashery.unitType,
    ],
    ["SKU", selectedOption?.sku || product.sku],
  ];

  return rows
    .map(([label, value]) => [label, safeText(value)])
    .filter(([, value]) => value);
}

function getAvailabilityText({
  selectedOption,
  hasStock,
  maxQty,
  available,
}) {
  const state = safeText(
    selectedOption?.purchaseState ||
      selectedOption?.unavailableState ||
      selectedOption?.reason ||
      selectedOption?.status ||
      selectedOption?.stockStatus,
  ).toUpperCase();

  const status = safeText(
    selectedOption?.status || selectedOption?.stockStatus,
  ).toLowerCase();

  if (
    state === "SHARED_STOCK_BLOCKED_BY_CART" ||
    status === "shared_stock_blocked_by_cart" ||
    status === "shared_stock_exhausted_in_cart"
  ) {
    return "Shared roll stock is already allocated to other cuts in your basket.";
  }

  if (status === "shared_stock_reduced_by_cart") {
    if (maxQty === 1) return "Only 1 more can be added from this shared roll.";
    if (maxQty > 1 && maxQty <= 3) {
      return `Only ${maxQty} more can be added from this shared roll.`;
    }
    return "Available from shared roll stock.";
  }

  if (state === "MAX_IN_CART" || status === "max_in_cart" || maxQty <= 0) {
    return "Maximum quantity already in your basket.";
  }

  if (state === "RESERVED") return "This selection is temporarily reserved.";
  if (state === "COMING_SOON") return "Coming soon.";
  if (state === "SOLD_OUT" || status === "out_of_stock") return "Sold out.";
  if (state === "UNAVAILABLE") return "Unavailable.";

  if (!hasStock) return "Unavailable.";

  const safeAvailable = Math.floor(Number(available || 0));

  if (safeAvailable === 1) return "Only 1 available";
  if (safeAvailable > 1 && safeAvailable <= 3) {
    return `Only ${safeAvailable} available`;
  }

  return "In stock";
}

function mergeVariantIntoSelectedOption(selectedOption, selectedVariant) {
  if (!selectedOption) return null;
  if (!selectedVariant) return selectedOption;

  const variantImages = Array.isArray(selectedVariant.images)
    ? selectedVariant.images
    : [];
  const optionImages = Array.isArray(selectedOption.images)
    ? selectedOption.images
    : [];
  const images = variantImages.length ? variantImages : optionImages;

  return {
    ...selectedOption,
    ...selectedVariant,
    images,

    label: safeText(selectedVariant.name) || selectedOption.label,
    variantName: safeText(selectedVariant.name),
    value: selectedOption.value,

    image: getImageUrl(images[0]) || selectedOption.image,
    shortDescription: safeText(selectedVariant.shortDescription),
    price: Number(selectedVariant.price ?? selectedOption.price ?? 0),
  };
}

function buildSeoProduct({
  product = {},
  selectedOption = null,
  selectedPrice = 0,
  gallery = [],
  hasStock = false,
  url = "",
  shopKey = "",
}) {
  return buildProductSeo({
    product,
    selectedOption,
    selectedPrice,
    gallery,
    hasStock,
    url,
    shopKey,
  });
}

export default function ProductScreenMobile() {
  const { shopKey = "", identifier = "" } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();

  const productBySlugState = useSelector((state) => state.productBySlug || {});
  const productDetailsState = useSelector(
    (state) => state.productDetails || {},
  );
  const userAuth = useSelector((state) => state.userAuth || {});

  const product =
    getProductFromState(productBySlugState) ||
    getProductFromState(productDetailsState);

  const loading = Boolean(
    productBySlugState.loading || productDetailsState.loading,
  );

  const error = product
    ? ""
    : getErrorText(productBySlugState.error || productDetailsState.error);

  const productId = safeText(product?._id || product?.id);
  const userId = safeText(userAuth.userInfo?.id || userAuth.userInfo?._id);
  const isAuthenticated = Boolean(
    userAuth.checked && userAuth.isAuthenticated && userId,
  );
  const mtmStep = normalizeMtmStep(searchParams.get("mtmStep"));
  const isMadeToMeasureMode = Boolean(
    mtmStep && getMtmShopForStep(mtmStep) === shopKey,
  );

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectingForMtm, setSelectingForMtm] = useState(false);
  const [madeToMeasureFeedback, setMadeToMeasureFeedback] = useState("");
  const [basketFeedback, setBasketFeedback] = useState({
    tone: "",
    message: "",
  });

  const options = useMemo(
    () =>
      Array.isArray(product?.purchasableOptions)
        ? product.purchasableOptions.filter(Boolean)
        : [],
    [product],
  );

  const displayableOptions = useMemo(
    () => options.filter(isOptionDisplayable),
    [options],
  );

  const selectedOption = useMemo(() => {
    if (!displayableOptions.length) return null;

    return (
      (selectedVariant
        ? displayableOptions.find((option) =>
            optionMatchesVariant(option, selectedVariant),
          )
        : null) ||
      displayableOptions.find(isOptionAddable) ||
      displayableOptions.find(isOptionSelectable) ||
      displayableOptions[0]
    );
  }, [displayableOptions, selectedVariant]);

  const activeSelectedVariant = useMemo(
    () =>
      selectedVariant || getVariantForOption(product || {}, selectedOption),
    [product, selectedOption, selectedVariant],
  );

  const selectedOptionWithVariant = useMemo(
    () =>
      mergeVariantIntoSelectedOption(selectedOption, activeSelectedVariant),
    [activeSelectedVariant, selectedOption],
  );

  const displayProductName =
    safeText(activeSelectedVariant?.name) || safeText(product?.name);

  const selectedOptionKey = useMemo(
    () => (selectedOption ? getOptionKey(selectedOption) : ""),
    [selectedOption],
  );

  const gallery = useMemo(
    () => buildGallery(product || {}, selectedOptionWithVariant),
    [product, selectedOptionWithVariant],
  );

  const selectedOptionWithVariantAddable = useMemo(
    () => isOptionAddable(selectedOptionWithVariant),
    [selectedOptionWithVariant],
  );

  const colourItems = useMemo(
    () => buildColourItems(product || {}, displayableOptions),
    [product, displayableOptions],
  );

  const sizeItems = useMemo(
    () => buildSizeItems(displayableOptions),
    [displayableOptions],
  );

  const selectedPrice =
    getFirstPositivePrice(
      selectedOptionWithVariant?.price,
      selectedOption?.price,
      getProductDisplayPriceValue(product),
      product?.priceFrom,
      product?.price,
    ) ?? 0;

  const available = Number(
    selectedOptionWithVariant?.available ??
      selectedOption?.available ??
      product?.available ??
      0,
  );

  const rawMaxQty = Number(
    activeSelectedVariant?.maxAddableQty ??
      activeSelectedVariant?.remainingAddableQty ??
      activeSelectedVariant?.available ??
      selectedOptionWithVariant?.maxAddableQty ??
      selectedOptionWithVariant?.remainingAddableQty ??
      selectedOption?.maxAddableQty ??
      selectedOption?.remainingAddableQty ??
      available ??
      0,
  );

  const maxQty = selectedOptionWithVariantAddable
    ? Math.max(
        0,
        Math.min(
          Math.floor(Number.isFinite(rawMaxQty) ? rawMaxQty : 0),
          MAX_QTY_CAP,
        ),
      )
    : 0;

  const hasStock = selectedOptionWithVariantAddable && maxQty > 0;

  const selectedCanPurchase =
    selectedOptionWithVariantAddable ||
    hasExplicitAddableFlag(selectedOptionWithVariant);

  const canPurchase =
    Boolean(productId) &&
    Boolean(selectedOptionWithVariant) &&
    selectedOptionWithVariantAddable &&
    selectedCanPurchase &&
    hasStock &&
    qty >= 1 &&
    qty <= maxQty &&
    !addingToCart;

  const availabilityText = useMemo(
    () =>
      getAvailabilityText({
        selectedOption: selectedOptionWithVariant,
        hasStock,
        maxQty,
        available,
      }),
    [available, hasStock, maxQty, selectedOptionWithVariant],
  );

  const productEligibleForMtm = Boolean(
    product && getMtmEligibilityForStep(product, mtmStep),
  );
  const madeToMeasureRequiresStock = Boolean(
    mtmStep && mtmStep !== MTM_STEPS.GARMENT,
  );
  const madeToMeasureAvailabilityFeedback =
    madeToMeasureRequiresStock && !canPurchase
      ? availabilityText || "Out of stock"
      : "";

  const madeToMeasureCanSelect = Boolean(
    isMadeToMeasureMode &&
      productId &&
      productEligibleForMtm &&
      (!madeToMeasureRequiresStock || canPurchase) &&
      !selectingForMtm,
  );

  const madeToMeasureStepLabel = getMtmSelectionActionLabel(mtmStep);

  const selectedColourLabel = useMemo(() => {
    const selectedColourKey = getOptionColourKey(selectedOptionWithVariant);

    const selectedColour = colourItems.find((item) => {
      if (selectedColourKey && item.key === selectedColourKey) return true;
      return item.optionKeys?.includes(selectedOptionKey);
    });

    return selectedColour?.label || colourItems[0]?.label || "";
  }, [colourItems, selectedOptionKey, selectedOptionWithVariant]);

  const selectedSizeLabel = useMemo(() => {
    const selectedSizeKey = getOptionSizeKey(selectedOptionWithVariant);

    const selectedSize = sizeItems.find((item) => {
      if (selectedSizeKey && item.key === selectedSizeKey) return true;
      return item.optionKeys?.includes(selectedOptionKey);
    });

    return selectedSize?.label || "";
  }, [selectedOptionKey, selectedOptionWithVariant, sizeItems]);

  const descriptionParagraphs = useMemo(
    () => getDescriptionParagraphs(product?.description),
    [product?.description],
  );

  const specificationRows = useMemo(
    () => buildSpecificationRows(product || {}, selectedOptionWithVariant),
    [product, selectedOptionWithVariant],
  );

  const optionLabel = useMemo(
    () => getPurchaseOptionLabel(product || {}),
    [product],
  );

  const canonicalPath = `/shop/${shopKey}/product/${identifier}`;

  const seoTitle = useMemo(
    () =>
      buildProductSeoTitle({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoDescription = useMemo(
    () =>
      buildProductSeoDescription({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoKeywords = useMemo(
    () =>
      buildProductSeoKeywords({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoProduct = useMemo(
    () =>
      buildSeoProduct({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        selectedPrice,
        gallery,
        hasStock,
        url: canonicalPath,
        shopKey,
      }),
    [
      canonicalPath,
      gallery,
      hasStock,
      product,
      selectedOptionWithVariant,
      selectedPrice,
      shopKey,
    ],
  );

  useEffect(() => {
    if (!shopKey || !identifier) return undefined;

    dispatch(getProductByIdentifier(shopKey, identifier)).catch(
      () => undefined,
    );

    return () => {
      dispatch(resetProductBySlug());
      dispatch(resetProductDetails());
      dispatch(resetProductResolve());
      dispatch(resetCartError());
    };
  }, [dispatch, shopKey, identifier]);

  useEffect(() => {
    if (!displayableOptions.length) return;

    const queryOption = displayableOptions.find(
      (option) =>
        optionMatchesQuery(option, searchParams) && isOptionSelectable(option),
    );

    const defaultOption =
      queryOption ||
      displayableOptions.find((option) => {
        const defaultValue = product?.defaultPurchasableOption || {};
        const params = getOptionParams(option);

        return (
          params.variantSku ===
            safeText(defaultValue.variantSku).toUpperCase() &&
          params.variantType ===
            safeText(defaultValue.variantType).toLowerCase() &&
          isOptionSelectable(option)
        );
      }) ||
      displayableOptions.find(isOptionAddable) ||
      displayableOptions.find(isOptionSelectable) ||
      displayableOptions[0];

    const nextVariant = getVariantForOption(product || {}, defaultOption);
    setSelectedVariant((current) =>
      current === nextVariant ? current : nextVariant,
    );
  }, [
    displayableOptions,
    product,
    product?.defaultPurchasableOption,
    searchParams,
    searchParamsKey,
  ]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [gallery]);

  useEffect(() => {
    if (maxQty <= 0) return;
    if (qty <= maxQty) return;

    setQty(maxQty);
  }, [maxQty, qty]);

  const updateUrlSelection = useCallback(
    (option, nextQty = qty) => {
      const params = getOptionParams(option);
      const next = new URLSearchParams(searchParams);

      if (params.variantSku) next.set("variantSku", params.variantSku);
      else next.delete("variantSku");

      if (params.variantType) next.set("variantType", params.variantType);
      else next.delete("variantType");

      if (nextQty > 1) next.set("qty", String(nextQty));
      else next.delete("qty");

      setSearchParams(next, { replace: true });
    },
    [qty, searchParams, setSearchParams],
  );

  const selectOption = useCallback(
    (option) => {
      if (!option || !isOptionSelectable(option)) return;

      setBasketFeedback({ tone: "", message: "" });
      setSelectedVariant(getVariantForOption(product || {}, option));
      setQty(1);
      updateUrlSelection(option, 1);
    },
    [product, updateUrlSelection],
  );

  const handleChoiceSelect = useCallback(
    (item) => {
      if (!item?.isSelectable) return;

      const candidates = getOptionsForChoice(item, displayableOptions);

      const nextOption = pickBestOption(candidates, selectedOptionWithVariant);

      if (nextOption && isOptionSelectable(nextOption)) {
        selectOption(nextOption);
      }
    },
    [displayableOptions, selectOption, selectedOptionWithVariant],
  );

  const handleQtyChange = useCallback(
    (nextQty) => {
      const safeQty = Math.min(
        Math.max(toPositiveInt(nextQty, 1), 1),
        maxQty || 1,
      );

      setBasketFeedback({ tone: "", message: "" });
      setQty(safeQty);

      if (selectedOptionWithVariant) {
        updateUrlSelection(selectedOptionWithVariant, safeQty);
      }
    },
    [maxQty, selectedOptionWithVariant, updateUrlSelection],
  );

  const handleAddToBasket = useCallback(async () => {
    if (!canPurchase || !productId || !selectedOptionWithVariant) return;

    const selection = getOptionParams(selectedOptionWithVariant);

    setAddingToCart(true);
    setBasketFeedback({ tone: "", message: "" });
    dispatch(resetCartError());

    try {
      const result = await dispatch(
        addToCart({
          productId,
          qty,
          variantSku: selection.variantSku || "",
          variantType: selection.variantType || "",
        }),
      );

      setBasketFeedback({
        tone: "success",
        message: result?.message || "Added to basket.",
      });
    } catch (cartError) {
      setBasketFeedback({
        tone: "danger",
        message: getSafeUserMessage(
          cartError?.message,
          "We could not add this product to your basket right now. Please try again later.",
        ),
      });
    } finally {
      setAddingToCart(false);
    }
  }, [canPurchase, dispatch, productId, qty, selectedOptionWithVariant]);

  const handleSelectForMadeToMeasure = useCallback(async () => {
    if (!isMadeToMeasureMode) return;

    if (!isAuthenticated) {
      const redirect = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }

    if (!productEligibleForMtm) {
      setMadeToMeasureFeedback(
        "This product is not currently available for made to measure.",
      );
      return;
    }

    if (madeToMeasureRequiresStock && !canPurchase) {
      setMadeToMeasureFeedback(
        madeToMeasureAvailabilityFeedback || "Out of stock",
      );
      return;
    }

    setSelectingForMtm(true);
    setMadeToMeasureFeedback("");

    try {
      const result = await dispatch(
        selectMadeToMeasureDraftProduct(mtmStep, {
          productId,
          quantity: qty,
          selectedOption: selectedOptionWithVariant,
        }),
      );

      const nextStep = getMtmReturnStepAfterSelection(mtmStep);

      navigate(`/made-to-measure?step=${encodeURIComponent(nextStep)}`, {
        state: {
          message:
            result?.message ||
            "Selection saved to your made-to-measure draft.",
        },
      });
    } catch (error) {
      setMadeToMeasureFeedback(
        getSafeUserMessage(
          error?.message,
          "We could not save this made-to-measure selection. Please try again later.",
        ),
      );
    } finally {
      setSelectingForMtm(false);
    }
  }, [
    canPurchase,
    dispatch,
    isAuthenticated,
    isMadeToMeasureMode,
    madeToMeasureAvailabilityFeedback,
    madeToMeasureRequiresStock,
    mtmStep,
    navigate,
    productEligibleForMtm,
    productId,
    qty,
    selectedOptionWithVariant,
  ]);

  if (loading) {
    return (
      <main className="product-screen-mobile product-screen-mobile--state">
        <Loader />
      </main>
    );
  }

  if (error) {
    return (
      <main className="product-screen-mobile product-screen-mobile--state">
        <Message variant="danger">{error}</Message>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="product-screen-mobile product-screen-mobile--state">
        <Message variant="info">Product not found.</Message>
      </main>
    );
  }

  return (
    <main className="product-screen-mobile">
      <Meta
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={gallery[0]?.url || product.defaultImage}
        url={canonicalPath}
        product={seoProduct}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: titleCase(shopKey), url: `/shop/${shopKey}` },
          {
            name: displayProductName,
            url: canonicalPath,
          },
        ]}
      />

      <div className="product-screen-mobile__inner">
        <section
          className="product-screen-mobile__gallery"
          aria-label="Product images"
        >
          <ImageZoom
            images={gallery}
            productName={displayProductName}
            selectedImageIndex={selectedImageIndex}
            onImageChange={setSelectedImageIndex}
          />
        </section>

        <section
          className="product-screen-mobile__purchase"
          aria-label="Product purchase information"
        >
          <PurchaseBox
            product={{ ...product, name: displayProductName }}
            brand={FALLBACK_BRAND}
            selectedPrice={selectedPrice}
            selectedOption={selectedOptionWithVariant}
            selectedOptionKey={selectedOptionKey}
            optionLabel={optionLabel}
            colourItems={colourItems}
            sizeItems={sizeItems}
            options={displayableOptions}
            selectedColourLabel={selectedColourLabel}
            selectedSizeLabel={selectedSizeLabel}
            hasStock={hasStock}
            availabilityText={availabilityText}
            qty={qty}
            maxQty={maxQty}
            resolveLoading={false}
            resolveError=""
            addingToCart={addingToCart}
            basketFeedback={basketFeedback}
            canPurchase={canPurchase}
            madeToMeasureMode={isMadeToMeasureMode}
            madeToMeasureStepLabel={madeToMeasureStepLabel}
            madeToMeasureCanSelect={madeToMeasureCanSelect}
            madeToMeasureLoading={selectingForMtm}
            madeToMeasureFeedback={
              madeToMeasureFeedback ||
              madeToMeasureAvailabilityFeedback ||
              (isMadeToMeasureMode && !isAuthenticated
                ? "Sign in to save this selection to your made-to-measure draft."
                : "")
            }
            descriptionParagraphs={descriptionParagraphs}
            specificationRows={specificationRows}
            onChoiceSelect={handleChoiceSelect}
            onOptionSelect={selectOption}
            onQtyChange={handleQtyChange}
            onAddToBasket={handleAddToBasket}
            onSelectForMadeToMeasure={handleSelectForMadeToMeasure}
          />
        </section>

        <ProductRecommendations productId={productId} limit={8} />
      </div>
    </main>
  );
}
